// Typed API calls over the shared axios client. Every response is the uniform
// envelope { success, data }; unwrap() throws on the error shape.
import type { AxiosResponse } from 'axios';
import type { ApiResponse, ApiKeyInfo, ApiKeyCreated } from '@rolefit/shared';
import type { SubScoreKey } from '@rolefit/domain/fit';
import { api } from './api.js';

function unwrap<T>(res: AxiosResponse<ApiResponse<T>>): T {
  if (!res.data.success) throw new Error(res.data.error);
  return res.data.data;
}

export type SubScores = Partial<Record<SubScoreKey, number | null>>;

export interface RoleRow {
  id: string;
  company: string | null;
  title: string | null;
  sourceUrl: string | null;
  source: string | null;
  work_mode: string | null;
  locations: string[];
  status: string;
  has_package: boolean;
  subScores: SubScores;
}

export interface RoleDetail {
  id: string;
  company: string | null;
  title: string | null;
  jd_text: string;
  source_url: string | null;
  sub_scores: SubScores;
  evidence: Array<{ requirement: string; evidence: string; sub_score_ref: string; score?: number | null }>;
  scored: boolean;
}

export interface ProfilePayload {
  profile_id: string;
  confirmed: boolean;
  structured: unknown;
  kpi_weights: Partial<Record<SubScoreKey, number>>;
  preferences: Record<string, unknown>;
  location: string | null;
  work_auth: Record<string, unknown>;
}

export interface GuardrailCheck {
  name: string;
  passed: boolean;
  detail: unknown;
}
export interface GuardrailReport {
  passed: boolean;
  checks: GuardrailCheck[];
  flags: string[];
}

export interface PackagePayload {
  package_id: string;
  status: string;
  cv_md: string | null;
  notes_md: string | null;
  cover_md: string | null;
  guardrail_report: GuardrailReport | null;
  has_pdf?: boolean;
}

export interface OutreachPayload {
  message_id: string;
  subject: string | null;
  body_md: string;
  status: string;
  guardrail_report: GuardrailReport | null;
}

export interface GmailConnection {
  scopes: string[];
  can_read: boolean;
  can_send: boolean;
  can_draft: boolean;
}

export interface SuggestionRow {
  id: string;
  proposed_state: string | null;
  email_type: string | null;
  summary: string | null;
  confidence: number;
  company: string | null;
  title: string | null;
  is_new: boolean;
  subject: string | null;
  from: string | null;
}

export interface RoleMessage {
  email_type: string | null;
  proposed_state: string | null;
  status: string;
  subject: string | null;
  from: string | null;
  snippet: string | null;
  received_ts: string | null;
}

export interface ReadLogItem {
  subject: string | null;
  from: string | null;
  received_ts: string | null;
  type: string | null;
  confidence: number | null;
  company: string | null;
  suggestion: string | null; // pending | accepted | dismissed | null
}
export interface ReadLog {
  total: number;
  dropped: number;
  relevant: number;
  items: ReadLogItem[];
}

export interface ApplicationRow {
  application_id: string;
  role_id: string;
  state: string;
  next_action: string | null;
  due_ts: string | null;
  company?: string | null;
  title?: string | null;
  source_url?: string | null;
}

export const queries = {
  async listRoles(): Promise<RoleRow[]> {
    return (await api.get<ApiResponse<{ roles: RoleRow[] }>>('/api/roles').then(unwrap)).roles;
  },
  async getRole(id: string): Promise<RoleDetail> {
    return api.get<ApiResponse<RoleDetail>>(`/api/roles/${id}`).then(unwrap);
  },
  // Soft-delete a single role (and its applications).
  async deleteRole(id: string): Promise<{ deleted: boolean }> {
    return api.delete<ApiResponse<{ deleted: boolean }>>(`/api/roles/${id}`).then(unwrap);
  },
  async ingestRole(body: {
    jd_text?: string;
    company?: string;
    title?: string;
    source_url?: string;
  }): Promise<{ role_id: string; deduped: boolean }> {
    return api.post<ApiResponse<{ role_id: string; deduped: boolean }>>('/api/roles/ingest', body).then(unwrap);
  },
  async ingestGreenhouse(board_token: string): Promise<{ ingested: number; deduped: number; role_ids: string[] }> {
    return api
      .post<ApiResponse<{ ingested: number; deduped: number; role_ids: string[] }>>('/api/roles/ingest/greenhouse', {
        board_token,
      })
      .then(unwrap);
  },
  async scoreRole(roleId: string): Promise<{ role_id: string; sub_scores: SubScores }> {
    return api
      .post<ApiResponse<{ role_id: string; sub_scores: SubScores }>>('/api/score', { role_id: roleId })
      .then(unwrap);
  },
  async generatePackage(roleId: string): Promise<{ package_id: string; status: string }> {
    return api
      .post<ApiResponse<{ package_id: string; status: string }>>('/api/package', { role_id: roleId })
      .then(unwrap);
  },
  async getPackage(roleId: string): Promise<PackagePayload | null> {
    return api.get<ApiResponse<PackagePayload | null>>(`/api/package/${roleId}`).then(unwrap);
  },
  // Rebuild from hand-edited Markdown (no model call): re-runs deterministic
  // guardrails and rebuilds the PDF.
  async rebuildPackage(roleId: string, body: { cv_md: string }): Promise<{ package_id: string; status: string }> {
    return api.patch<ApiResponse<{ package_id: string; status: string }>>(`/api/package/${roleId}`, body).then(unwrap);
  },
  async downloadPackagePdf(roleId: string): Promise<Blob> {
    const res = await api.get(`/api/package/${roleId}/pdf`, { responseType: 'blob' });
    return res.data as Blob;
  },
  async getConnections(): Promise<{ mailbox_enabled: boolean; gmail: GmailConnection | null }> {
    return api
      .get<ApiResponse<{ mailbox_enabled: boolean; gmail: GmailConnection | null }>>('/api/connections')
      .then(unwrap);
  },
  async startGmailConnect(scope: 'draft' | 'send' | 'read'): Promise<{ url: string }> {
    return api.post<ApiResponse<{ url: string }>>('/api/connections/gmail/start', { scope }).then(unwrap);
  },
  async scanInbox(): Promise<{
    scanned: number;
    candidates: number;
    classified: number;
    dropped: number;
    suggestions: number;
  }> {
    return api
      .post<
        ApiResponse<{ scanned: number; candidates: number; classified: number; dropped: number; suggestions: number }>
      >('/api/mailtrack/scan', {})
      .then(unwrap);
  },
  async getSuggestions(): Promise<{ suggestions: SuggestionRow[] }> {
    return api.get<ApiResponse<{ suggestions: SuggestionRow[] }>>('/api/mailtrack/suggestions').then(unwrap);
  },
  async getReadLog(): Promise<ReadLog> {
    return api.get<ApiResponse<ReadLog>>('/api/mailtrack/read-log').then(unwrap);
  },
  async acceptSuggestion(id: string): Promise<{ id: string; status: string }> {
    return api
      .post<ApiResponse<{ id: string; status: string }>>(`/api/mailtrack/suggestions/${id}/accept`, {})
      .then(unwrap);
  },
  async dismissSuggestion(id: string): Promise<{ id: string; status: string }> {
    return api
      .post<ApiResponse<{ id: string; status: string }>>(`/api/mailtrack/suggestions/${id}/dismiss`, {})
      .then(unwrap);
  },
  async getRoleMessages(roleId: string): Promise<{ messages: RoleMessage[] }> {
    return api.get<ApiResponse<{ messages: RoleMessage[] }>>(`/api/mailtrack/role/${roleId}/messages`).then(unwrap);
  },
  async setMailboxAutoApply(auto_apply: boolean): Promise<{ auto_apply: boolean }> {
    return api.patch<ApiResponse<{ auto_apply: boolean }>>('/api/mailtrack/settings', { auto_apply }).then(unwrap);
  },
  async disconnectMailbox(): Promise<{ disconnected: number }> {
    return api.delete<ApiResponse<{ disconnected: number }>>('/api/mailtrack/connection').then(unwrap);
  },
  async approveOutreach(roleId: string): Promise<OutreachPayload> {
    return api.post<ApiResponse<OutreachPayload>>(`/api/outreach/${roleId}/approve`, {}).then(unwrap);
  },
  async sendOutreach(roleId: string, to: string): Promise<OutreachPayload & { action?: string }> {
    return api
      .post<ApiResponse<OutreachPayload & { action?: string }>>(`/api/outreach/${roleId}/send`, { to })
      .then(unwrap);
  },
  async getOutreach(roleId: string): Promise<OutreachPayload | null> {
    return api.get<ApiResponse<OutreachPayload | null>>(`/api/outreach/${roleId}`).then(unwrap);
  },
  async generateOutreach(roleId: string): Promise<OutreachPayload> {
    return api.post<ApiResponse<OutreachPayload>>(`/api/outreach/${roleId}/draft`, {}).then(unwrap);
  },
  // Save a hand-edited draft (re-runs deterministic guardrails, no model call).
  async saveOutreach(roleId: string, body: { subject?: string; body_md: string }): Promise<OutreachPayload> {
    return api.patch<ApiResponse<OutreachPayload>>(`/api/outreach/${roleId}`, body).then(unwrap);
  },
  async getProfile(): Promise<ProfilePayload | null> {
    return api.get<ApiResponse<ProfilePayload | null>>('/api/profile').then(unwrap);
  },
  // Wipe all of the user's data (roles, applications, packages, mailbox, etc.),
  // keeping the login.
  async resetAccount(): Promise<{ reset: boolean }> {
    return api.delete<ApiResponse<{ reset: boolean }>>('/api/account/data').then(unwrap);
  },
  // Personal API keys for the browser extension (scoped to ingest).
  async listApiKeys(): Promise<ApiKeyInfo[]> {
    return (await api.get<ApiResponse<{ keys: ApiKeyInfo[] }>>('/api/api-keys').then(unwrap)).keys;
  },
  async createApiKey(name: string): Promise<ApiKeyCreated> {
    return api.post<ApiResponse<ApiKeyCreated>>('/api/api-keys', { name }).then(unwrap);
  },
  async revokeApiKey(id: string): Promise<{ revoked: boolean }> {
    return api.delete<ApiResponse<{ revoked: boolean }>>(`/api/api-keys/${id}`).then(unwrap);
  },
  async setWeights(kpi_weights: Record<string, number>): Promise<ProfilePayload> {
    return api.patch<ApiResponse<ProfilePayload>>('/api/profile', { kpi_weights }).then(unwrap);
  },
  async confirmProfile(structured?: unknown): Promise<ProfilePayload> {
    return api.post<ApiResponse<ProfilePayload>>('/api/profile/confirm', { structured }).then(unwrap);
  },
  async importCv(file: File): Promise<ProfilePayload> {
    const form = new FormData();
    form.append('file', file);
    return api.post<ApiResponse<ProfilePayload>>('/api/profile/import', form).then(unwrap);
  },
  // Import a draft profile from a URL (personal site / public resume page).
  async importCvUrl(url: string): Promise<ProfilePayload> {
    return api.post<ApiResponse<ProfilePayload>>('/api/profile/import-url', { url }).then(unwrap);
  },
  // Import a draft profile from pasted text (e.g. copied from LinkedIn).
  async importCvText(text: string): Promise<ProfilePayload> {
    return api.post<ApiResponse<ProfilePayload>>('/api/profile/import-text', { text }).then(unwrap);
  },
  async listApplications(): Promise<ApplicationRow[]> {
    return (await api.get<ApiResponse<{ applications: ApplicationRow[] }>>('/api/applications').then(unwrap))
      .applications;
  },
  async createApplication(roleId: string): Promise<ApplicationRow> {
    return api.post<ApiResponse<ApplicationRow>>('/api/applications', { role_id: roleId }).then(unwrap);
  },
  async updateApplication(
    id: string,
    body: { state?: string; next_action?: string | null; due_ts?: string | null; note?: string },
  ): Promise<ApplicationRow> {
    return api.patch<ApiResponse<ApplicationRow>>(`/api/applications/${id}`, body).then(unwrap);
  },
};
